import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/ilm-cart';

/**
 * ilmStore book page behaviour (sections/ilm-product.liquid).
 *
 * <ilm-gallery>  swipeable cover gallery with thumbnails; goTo(mediaId) shows a variant's image.
 * <ilm-buy-box>  upgrades the product form: option buttons (the <select name="id"> stays the
 *                source of truth and is the no-JavaScript fallback), price / stock / savings
 *                updates, quantity stepper, delivery date estimate, WhatsApp order text and the
 *                mobile sticky "Add to basket" bar. Adding to the cart is handled by ilm-cart.js.
 */

class IlmGallery extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const track = this.querySelector('[data-ilm-gallery-track]');
    if (!track) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const slide = /** @type {HTMLElement} */ (entry.target);
          this.#mark(slide.dataset.mediaId || '');
        }
      },
      { root: track, threshold: 0.6 }
    );
    for (const slide of track.children) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  /** @param {string | number} mediaId */
  goTo(mediaId) {
    const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
    const track = this.querySelector('[data-ilm-gallery-track]');
    if (!(slide instanceof HTMLElement) || !(track instanceof HTMLElement)) return;
    const offset = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: getComputedStyle(track).direction === 'rtl' ? -Math.abs(offset) : offset });
    this.#mark(String(mediaId));
  }

  /** @param {string} mediaId */
  #mark(mediaId) {
    const thumbs = [...this.querySelectorAll('[data-ilm-gallery-thumb]')];
    thumbs.forEach((thumb, index) => {
      const current = thumb.getAttribute('data-ilm-gallery-thumb') === mediaId;
      if (current) {
        thumb.setAttribute('aria-current', 'true');
        const counter = this.querySelector('[data-ilm-gallery-index]');
        if (counter) counter.textContent = String(index + 1);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('[data-ilm-gallery-thumb]') : null;
    if (thumb) this.goTo(thumb.getAttribute('data-ilm-gallery-thumb') || '');
  };
}

/**
 * @typedef {{ id: number, options: string[], available: boolean, price: number, compare: number, title: string, media: number, qty: number | null }} Variant
 */

class IlmBuyBox extends HTMLElement {
  /** @type {Variant[]} */
  variants = [];
  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;
  /** @type {AbortController | null} */
  #events = null;

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const { signal } = this.#events;

    try {
      this.variants = JSON.parse(this.querySelector('[data-ilm-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    // Option buttons replace the plain variant <select> once this script runs.
    const fieldsets = this.querySelectorAll('[data-ilm-option]');
    if (fieldsets.length) {
      fieldsets.forEach((fieldset) => fieldset.removeAttribute('hidden'));
      this.classList.add('is-enhanced');
      this.#markUnavailable();
    }

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#renderDelivery();
    this.#setupSticky(signal);

    document.addEventListener(
      CartAddEvent.eventName,
      (event) => {
        const detail = /** @type {any} */ (event).detail;
        if (detail?.data?.source !== 'ilm-product') return;
        const sticky = /** @type {HTMLButtonElement | null} */ (this.#stickyBar()?.querySelector('[data-ilm-sticky-add]'));
        if (!sticky) return;
        const label = sticky.textContent;
        sticky.textContent = this.querySelector('[data-ilm-add-button]')?.getAttribute('data-label-added') || label;
        setTimeout(() => {
          sticky.textContent = label;
        }, 2200);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#stickyObserver?.disconnect();
    document.documentElement.style.removeProperty('--ilm-float-offset');
  }

  get form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-ilm-add-form]'));
  }

  get select() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-ilm-variant-select]'));
  }

  get currentVariant() {
    const id = Number(this.select?.value || this.form?.querySelector('input[name="id"]')?.getAttribute('value'));
    return this.variants.find((variant) => variant.id === id) || this.variants[0];
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target.matches('[data-ilm-option] input')) {
      const values = [...this.querySelectorAll('[data-ilm-option]')].map(
        (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value || ''
      );
      const fieldset = target.closest('[data-ilm-option]');
      const shown = fieldset?.querySelector('[data-ilm-option-value]');
      if (shown) shown.textContent = target.value;
      const variant = this.variants.find((v) => v.options.every((option, i) => option === values[i]));
      if (variant && this.select) this.select.value = String(variant.id);
      this.#markUnavailable();
      this.#update(variant || null);
    } else if (target.matches('[data-ilm-variant-select]')) {
      this.#update(this.currentVariant || null);
    } else if (target.matches('input[name="quantity"]')) {
      this.#clampQuantity();
      this.#updateWhatsApp(this.currentVariant || null);
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ilm-qty]') : null;
    if (!button) return;
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (!input) return;
    input.value = String((Number(input.value) || 1) + Number(button.getAttribute('data-ilm-qty')));
    this.#clampQuantity();
    this.#updateWhatsApp(this.currentVariant || null);
  };

  #clampQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (!input) return;
    const max = Number(input.max) || 99;
    input.value = String(Math.min(max, Math.max(1, Math.round(Number(input.value) || 1))));
  }

  /** Crosses out option values that have no available variant with the other selections. */
  #markUnavailable() {
    const fieldsets = [...this.querySelectorAll('[data-ilm-option]')];
    const selected = fieldsets.map((fs) => /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'))?.value || '');
    fieldsets.forEach((fieldset, index) => {
      for (const input of fieldset.querySelectorAll('input')) {
        const candidate = [...selected];
        candidate[index] = /** @type {HTMLInputElement} */ (input).value;
        const ok = this.variants.some((v) => v.available && v.options.every((option, i) => option === candidate[i]));
        input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
      }
    });
  }

  /** @param {Variant | null} variant */
  #update(variant) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ilm-add-button]'));
    const sticky = /** @type {HTMLButtonElement | null} */ (this.#stickyBar()?.querySelector('[data-ilm-sticky-add]'));
    const available = Boolean(variant?.available);
    const addLabel = this.dataset.labelAdd || 'Add to basket';
    const soldLabel = this.dataset.labelSoldOut || 'Sold out';

    for (const b of [button, sticky]) {
      if (!b) continue;
      b.disabled = !available;
      b.textContent = available ? addLabel : soldLabel;
      if (b === button) b.dataset.labelIdle = b.innerHTML;
    }
    const nowButton = this.querySelector('.ilm-buy__now-btn');
    if (nowButton instanceof HTMLElement) nowButton.hidden = !available;

    if (!variant) return;
    const price = formatMoney(variant.price);
    const priceEl = this.querySelector('[data-ilm-price]');
    if (priceEl) priceEl.textContent = price;
    const stickyPrice = this.#stickyBar()?.querySelector('[data-ilm-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = price;

    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ilm-compare]'));
    const saveEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ilm-save]'));
    const saving = variant.compare > variant.price ? variant.compare - variant.price : 0;
    if (compareEl) {
      compareEl.hidden = !saving;
      compareEl.textContent = saving ? formatMoney(variant.compare) : '';
    }
    if (saveEl) {
      saveEl.hidden = !saving;
      saveEl.textContent = saving
        ? (this.dataset.labelSave || '')
            .replace('[amount]', formatMoney(saving))
            .replace('[percent]', String(Math.floor((saving * 100) / variant.compare)))
        : '';
    }

    const stock = this.querySelector('[data-ilm-stock]');
    if (stock) {
      stock.textContent = available ? this.dataset.labelInStock || '' : soldLabel;
      stock.classList.toggle('is-out', !available);
    }

    const qty = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (qty) {
      qty.max = String(variant.qty && variant.qty > 0 ? variant.qty : 99);
      this.#clampQuantity();
    }

    if (variant.media) {
      const gallery = /** @type {IlmGallery | null} */ (document.querySelector('ilm-gallery'));
      gallery?.goTo(variant.media);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState(history.state, '', url);
    this.#updateWhatsApp(variant);
  }

  /** @param {Variant | null} variant */
  #updateWhatsApp(variant) {
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-ilm-wa]'));
    const template = this.dataset.waTemplate;
    if (!link || !template || !variant?.available) return;
    const qty = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'))?.value || '1';
    const title = variant.title && variant.title !== 'Default Title' ? `${this.dataset.productTitle} (${variant.title})` : this.dataset.productTitle || '';
    const message = template
      .replace('[title]', title)
      .replace('[qty]', qty)
      .replace('[price]', formatMoney(variant.price * Number(qty)))
      .replace('[url]', `${this.dataset.productUrl}?variant=${variant.id}`);
    try {
      const url = new URL(link.href);
      url.searchParams.set('text', message);
      link.href = url.toString();
    } catch {
      // Leave the server-rendered link in place.
    }
  }

  /** Shows "Get it between Thu, 17 Oct and Mon, 21 Oct" from the dispatch + transit days. */
  #renderDelivery() {
    const target = this.querySelector('[data-ilm-delivery] span');
    const label = this.dataset.labelDelivery;
    const min = Number(this.dataset.deliveryMin);
    const max = Number(this.dataset.deliveryMax);
    if (!target || !label || !min || !max) return;
    const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const day = (/** @type {number} */ offset) => {
      const date = new Date();
      date.setDate(date.getDate() + offset);
      return format.format(date);
    };
    target.textContent = label.replace('[from]', day(min)).replace('[to]', day(max));
  }

  #stickyBar() {
    return /** @type {HTMLElement | null} */ (this.closest('.ilm-pdp')?.querySelector('[data-ilm-sticky]') || null);
  }

  /** @param {AbortSignal} signal */
  #setupSticky(signal) {
    const bar = this.#stickyBar();
    const button = this.querySelector('[data-ilm-add-button]');
    if (!bar || !button) return;
    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.hidden = !show;
      if (show) document.documentElement.style.setProperty('--ilm-float-offset', `${bar.offsetHeight}px`);
      else document.documentElement.style.removeProperty('--ilm-float-offset');
    });
    this.#stickyObserver.observe(button);
    bar.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-ilm-sticky-add]')) return;
        const form = this.form;
        if (form && button instanceof HTMLButtonElement && !button.disabled) form.requestSubmit(button);
      },
      { signal }
    );
  }
}

if (!customElements.get('ilm-gallery')) customElements.define('ilm-gallery', IlmGallery);
if (!customElements.get('ilm-buy-box')) customElements.define('ilm-buy-box', IlmBuyBox);
